/* SOLVEX category marquee — an endlessly looping, swipeable category strip.
 *
 * The strip is a real horizontal scroller. The loop is made by cloning the
 * rendered list and wrapping scrollLeft by exactly one period (one list plus
 * one gap), so the jump lands on an identical frame. Moving scrollLeft rather
 * than transforming the track keeps swiping, trackpads and keyboard focus
 * working natively.
 *
 * The resting range is [period, 2 * period): the first clone is at the left
 * edge, so a visitor can swipe left as well as right without hitting a wall.
 * The original list sits just off to the left, which is where keyboard focus
 * scrolls to; wrapping is suspended while focus is inside so the focused card
 * is never moved out of view.
 *
 * Transform mode (data-marquee-mode="transform", used by the announcement bar
 * since 2026-09-28, audit PERF-01): the same clones and the same period, but
 * the motion is a CSS animation of the track's transform, run by the
 * compositor, instead of a scrollLeft write on every animation frame, forever.
 * The script only measures, then switches the animation on and off. The strip
 * is not a scroller in this mode, so it cannot be swiped.
 */
(function () {
  if (customElements.get('solvex-category-marquee')) return;

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var USER_HOLD_MS = 2500;

  class SolvexCategoryMarquee extends HTMLElement {
    constructor() {
      super();
      this.period = 0;
      this.position = 0;
      this.lastSet = 0;
      this.holdUntil = 0;
      this.hovering = false;
      this.editorHold = false;
      this.visible = true;
      this.placed = false;
      this.frame = null;
      this.lastTime = null;

      this.tick = this.tick.bind(this);
      this.sync = this.sync.bind(this);
      this.fill = this.fill.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onBlockDeselect = this.onBlockDeselect.bind(this);

      this.addEventListener('scroll', this.onScroll.bind(this), { passive: true });
      this.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse') this.hovering = true;
        this.updatePlay();
      });
      this.addEventListener('pointerleave', () => {
        this.hovering = false;
        this.updatePlay();
      });
      // A finger resting on the strip should hold it even before it scrolls.
      this.addEventListener('pointerdown', () => this.hold(), { passive: true });
      // The pause button (solvex-motion-toggle.js) stops the strip until pressed again.
      this.userPaused = false;
      this.addEventListener('solvex:motion', (event) => {
        this.userPaused = event.detail.paused;
        this.updatePlay();
      });
      this.addEventListener('focusin', () => this.updatePlay());
      this.addEventListener('focusout', () => setTimeout(() => this.updatePlay(), 0));
    }

    connectedCallback() {
      this.track = this.querySelector('[data-marquee-track]');
      this.group = this.querySelector('[data-marquee-group]');
      if (!this.track || !this.group || !this.group.children.length) return;
      this.transformMode = this.dataset.marqueeMode === 'transform';

      document.addEventListener('visibilitychange', this.sync);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      if (reducedMotion.addEventListener) reducedMotion.addEventListener('change', this.sync);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.fill);
        this.resizeObserver.observe(this);
      }

      if ('IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver((entries) => {
          this.visible = entries[entries.length - 1].isIntersecting;
          this.sync();
        });
        this.intersectionObserver.observe(this);
      }

      this.fill();
    }

    disconnectedCallback() {
      if (this.frame != null) cancelAnimationFrame(this.frame);
      this.frame = null;
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.intersectionObserver) this.intersectionObserver.disconnect();
      document.removeEventListener('visibilitychange', this.sync);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      if (reducedMotion.removeEventListener) reducedMotion.removeEventListener('change', this.sync);
    }

    // Clone the list until the scroll range covers 2 * period plus one screen.
    fill() {
      var width = this.clientWidth;
      var groupWidth = this.group.offsetWidth;
      if (!width || !groupWidth) return;

      var clones = this.track.querySelectorAll('[data-marquee-clone]').length;
      var needed = Math.ceil(width / groupWidth) + 2;
      for (var i = clones; i < needed; i++) {
        this.track.appendChild(this.makeClone());
      }

      var firstClone = this.track.querySelector('[data-marquee-clone]');
      this.period = firstClone.offsetLeft - this.group.offsetLeft;
      if (this.period <= 0) return;

      if (this.transformMode) {
        // One period per cycle at the configured speed, so the loop wraps on
        // an identical frame exactly as the scrollLeft version did.
        this.style.setProperty('--marquee-period', this.period + 'px');
        this.style.setProperty('--marquee-duration', (this.period / this.speed()).toFixed(3) + 's');
        this.classList.add('is-transform-marquee');
        this.sync();
        return;
      }

      if (!this.placed) {
        this.position = this.period;
        this.placed = true;
      } else {
        this.position = this.scrollLeft;
      }
      if (!this.hasKeyboardFocus()) this.normalise();
      this.apply();
      this.sync();
    }

    makeClone() {
      var clone = this.group.cloneNode(true);
      clone.removeAttribute('data-marquee-group');
      clone.setAttribute('data-marquee-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      // Not `inert`: most of the cards a visitor sees are clones and must stay clickable.
      clone.querySelectorAll('a, button, input, [tabindex]').forEach(function (element) {
        element.setAttribute('tabindex', '-1');
      });
      clone.querySelectorAll('[data-shopify-editor-block], [id]').forEach(function (element) {
        element.removeAttribute('data-shopify-editor-block');
        element.removeAttribute('id');
      });
      return clone;
    }

    normalise() {
      if (this.period <= 0) return;
      while (this.position >= this.period * 2) this.position -= this.period;
      if (this.position <= 0) this.position += this.period;
    }

    sync() {
      var run = this.period > 0 && this.visible && !document.hidden && !reducedMotion.matches;
      if (this.transformMode) {
        this.running = run;
        this.updatePlay();
        return;
      }
      if (run && this.frame == null) {
        this.lastTime = null;
        this.frame = requestAnimationFrame(this.tick);
      } else if (!run && this.frame != null) {
        cancelAnimationFrame(this.frame);
        this.frame = null;
      }
    }

    held(now) {
      return (
        this.userPaused ||
        this.hovering ||
        this.editorHold ||
        now < this.holdUntil ||
        this.hasKeyboardFocus()
      );
    }

    /*
      Keyboard focus inside holds the strip so a tabbed-to link stays put.
      Focus left behind by a click or tap does not: the link just clicked, or
      the strip itself (Chrome makes a scroller focusable), used to stop the
      loop until the visitor clicked somewhere else.
    */
    hasKeyboardFocus() {
      var active = document.activeElement;
      if (!active || !this.contains(active)) return false;
      try {
        return active.matches(':focus-visible');
      } catch (error) {
        return true;
      }
    }

    hold() {
      this.holdUntil = performance.now() + USER_HOLD_MS;
      if (this.transformMode) {
        this.updatePlay();
        clearTimeout(this.holdTimer);
        this.holdTimer = setTimeout(() => this.updatePlay(), USER_HOLD_MS + 20);
      }
    }

    /*
      Transform mode: the scroll version re-checked held() on every frame;
      here the same conditions are re-checked when one of them changes.
      Keyboard focus also resets the track, so the focused link (in the
      original list, at the start) is in view rather than slid off the edge.
    */
    updatePlay() {
      if (!this.transformMode) return;
      var paused = !this.running || this.held(performance.now());
      this.toggleAttribute('data-marquee-paused', paused);
      var focused = this.hasKeyboardFocus();
      this.toggleAttribute('data-marquee-focused', focused);
      // Undo any scroll the browser made to show a focused link.
      if (!focused && this.scrollLeft) this.scrollLeft = 0;
    }

    tick(now) {
      // Cap the step so a throttled or backgrounded frame does not lurch.
      var elapsed = this.lastTime == null ? 0 : Math.min(now - this.lastTime, 64);
      this.lastTime = now;

      if (!this.held(now)) {
        this.position += (this.speed() * elapsed) / 1000;
        this.normalise();
        this.apply();
      }

      this.frame = requestAnimationFrame(this.tick);
    }

    speed() {
      return Math.max(Number(this.dataset.speed) || 35, 1);
    }

    apply() {
      this.scrollLeft = this.position;
      // Read back: the browser may round, and onScroll compares against this.
      this.lastSet = this.scrollLeft;
    }

    // Any scroll the script did not make is the visitor's: hold, then wrap.
    onScroll() {
      // Transform mode moves the track, not the scroll position; a scroll
      // here can only be the browser bringing a focused link into view.
      if (this.transformMode) return;
      if (this.period <= 0) return;
      var current = this.scrollLeft;
      if (Math.abs(current - this.lastSet) < 2) return;

      this.hold();
      this.position = current;
      if (!this.hasKeyboardFocus()) {
        this.normalise();
        if (this.position !== current) this.scrollLeft = this.position;
      }
      this.lastSet = this.scrollLeft;
      this.position = this.lastSet;
    }

    // Theme Editor: selecting a card block brings the original into view and holds it there.
    onBlockSelect(event) {
      if (!this.group || !this.group.contains(event.target)) return;
      this.editorHold = true;
      this.scrollLeft = event.target.offsetLeft;
      this.lastSet = this.scrollLeft;
      this.position = this.lastSet;
    }

    onBlockDeselect(event) {
      if (!this.group || !this.group.contains(event.target)) return;
      this.editorHold = false;
    }
  }

  customElements.define('solvex-category-marquee', SolvexCategoryMarquee);
})();
